import React, { useRef, useState } from 'react';

import { Web } from '@mui/icons-material';
import { Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Breadcrumbs, Button, FormTextField, InfoTextField, SubHeader } from '../../../../../components';
import {
  searchExpenseById,
  copyReceiptToExpense,
  uploadDocumentToExpense,
  firstItemWithDoc,
  getErrorMessage,
} from '../operations';

import './styles.scoped.css';

// Receipts live on the ExpenseItem line items, so derive the document name from
// there (not the top-level expense.documentName, which the app never populates — CP-815).
const documentNameFor = expense => {
  const name = firstItemWithDoc(expense)?.documentName;
  return name ? name.split('/').pop() : '';
};

const ExpenseDetails = ({ expense }) => (
  <>
    <div className="expense-details-grid">
      <InfoTextField
        label="Driver Name & Driver Number"
        required
        value={`${expense.driverName} - ${expense.driverId}`}
      />
      <InfoTextField label="Total To Be Billed" value={expense.totalToBeBilled} />
      <InfoTextField label="Code" value={expense.code} />
      <InfoTextField label="Status" value={expense.status} />
    </div>
    <div className="expense-details-grid">
      <InfoTextField
        label="Customer Name & Customer Number"
        required
        value={`${expense.customerName} - ${expense.customerId}`}
      />
      <InfoTextField label="Document Name">
        <span className="document-name">{documentNameFor(expense)}</span>
      </InfoTextField>
    </div>
  </>
);

const ExpenseSearch = ({ label, expenseId, onIdChange, onSearch, searching, expense, error }) => (
  <>
    <div className="search-row">
      <FormTextField
        label={label}
        value={expenseId}
        onChange={e => onIdChange(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && onSearch()}
        error={!!error}
        errorMessage={error}
        disabled={searching}
      />
      {searching ? (
        <Spinner animation="border" size="sm" />
      ) : (
        <Button variant="dark" onPress={onSearch} text="Search" />
      )}
    </div>
    {expense && <ExpenseDetails expense={expense} />}
  </>
);

const ReceiptExpense = () => {
  const navigate = useNavigate();
  const userId = useSelector(state => state.admin.user.id);
  const fileInputRef = useRef(null);

  const [sourceId, setSourceId] = useState('');
  const [sourceExpense, setSourceExpense] = useState(null);
  const [searchingSource, setSearchingSource] = useState(false);
  const [sourceError, setSourceError] = useState('');

  const [destId, setDestId] = useState('');
  const [destExpense, setDestExpense] = useState(null);
  const [searchingDest, setSearchingDest] = useState(false);
  const [destError, setDestError] = useState('');

  const [stagedDoc, setStagedDoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const handleSearchSource = async () => {
    if (!sourceId.trim()) {
      setSourceError('Please enter an Expense ID.');
      return;
    }
    setSearchingSource(true);
    setSourceError('');
    setSourceExpense(null);
    setStagedDoc(null);
    try {
      const result = await searchExpenseById(sourceId.trim());
      if (result) {
        setSourceExpense(result);
      } else {
        setSourceError('Source expense not found.');
      }
    } catch {
      setSourceError('Error searching for source expense.');
    } finally {
      setSearchingSource(false);
    }
  };

  const handleSearchDest = async () => {
    if (!destId.trim()) {
      setDestError('Please enter an Expense ID.');
      return;
    }
    setSearchingDest(true);
    setDestError('');
    setDestExpense(null);
    setStagedDoc(null);
    try {
      const result = await searchExpenseById(destId.trim());
      if (result) {
        setDestExpense(result);
      } else {
        setDestError('Destination expense not found.');
      }
    } catch {
      setDestError('Error searching for destination expense.');
    } finally {
      setSearchingDest(false);
    }
  };

  const handleCopyReceipt = () => {
    // Copy needs BOTH expenses. Validate on press and flash the error on both
    // fields at once rather than disabling the button (CP-787 pattern).
    setSaveError('');
    let invalid = false;
    if (!sourceExpense) {
      setSourceError('Please enter an Expense ID.');
      invalid = true;
    }
    if (!destExpense) {
      setDestError('Please enter an Expense ID.');
      invalid = true;
    }
    if (invalid) return;
    const sourceItem = firstItemWithDoc(sourceExpense);
    if (!sourceItem) {
      setSaveError('Source expense has no document to copy.');
      return;
    }
    // CP-815: only fill a MISSING receipt — block if the destination already has one (duplicate).
    if (firstItemWithDoc(destExpense)) {
      setSaveError('Destination expense already has a receipt — cannot copy (would duplicate).');
      return;
    }
    const displayName = sourceItem.documentName.split('/').pop() || sourceItem.documentName;
    setStagedDoc({ name: displayName, type: 'copy' });
  };

  const handleUploadClick = () => {
    setSaveError('');
    if (!destExpense) {
      setDestError('Search a DESTINATION expense first.');
      return;
    }
    // CP-815: only add a document to an expense that is MISSING one — prevent duplicates.
    if (firstItemWithDoc(destExpense)) {
      setSaveError('This expense already has a receipt — a separate document cannot be added.');
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileSelect = e => {
    const file = e.target.files[0];
    if (!file) return;
    setStagedDoc({ name: file.name, type: 'upload', file });
    e.target.value = '';
  };

  const handleSave = async () => {
    // Save stays enabled; validate on press and prompt inline rather than
    // blocking via a disabled button (CP-787 pattern).
    setSaveError('');
    if (!destId.trim()) {
      setDestError('Please enter an Expense ID.');
      return;
    }
    if (!stagedDoc) {
      setSaveError('Copy a receipt or upload a document before saving.');
      return;
    }
    setSaving(true);
    try {
      if (stagedDoc.type === 'copy') {
        await copyReceiptToExpense({ sourceExpenseId: sourceId, destinationExpenseId: destId, userId });
      } else {
        await uploadDocumentToExpense({ expenseId: destId, file: stagedDoc.file, userId });
      }
      navigate('/expense-entry/expense-maintenance');
    } catch (e) {
      setSaveError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SubHeader
        text="Expenses"
        buttons={[{ icon: <Web sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/dashboard') }]}
        popoverTitle="Need help with your Expenses?"
      />
      <Breadcrumbs
        leftTitle="Expense Maintenance"
        rightTitle="Missing Receipt Expense"
        route="/expense-entry/expense-maintenance"
      />
      <div className="wrap">
        <div className="card-tab">Reload Missing Receipt</div>
        <div className="maintenance-card">
          <div className="card-content">
            <ExpenseSearch
              label="Enter SOURCE Expense ID"
              expenseId={sourceId}
              onIdChange={val => {
                setSourceId(val);
                setStagedDoc(null);
              }}
              onSearch={handleSearchSource}
              searching={searchingSource}
              expense={sourceExpense}
              error={sourceError}
            />

            <div className="destination-section">
              <ExpenseSearch
                label="Enter DESTINATION Expense ID"
                expenseId={destId}
                onIdChange={val => {
                  setDestId(val);
                  setStagedDoc(null);
                }}
                onSearch={handleSearchDest}
                searching={searchingDest}
                expense={destExpense}
                error={destError}
              />
              {stagedDoc && <p className="document-name">{stagedDoc.name}</p>}
            </div>

            <div className="action-buttons">
              <Button
                variant="primary"
                onPress={handleCopyReceipt}
                fullWidth
                text="Copy Receipt to This Expense"
                disabled={saving}
              />
              <Button
                variant="outline"
                onPress={handleUploadClick}
                fullWidth
                text="Upload Separate Document"
                disabled={saving}
              />
              <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileSelect} />
              {saveError && <p className="error-text">{saveError}</p>}
            </div>
          </div>
          <div className="buttons-container">
            <Button variant="primary" onPress={handleSave} text="Save" disabled={saving} />
            <Button variant="outline" onPress={() => navigate('/expense-entry/expense-maintenance')} text="Cancel" />
          </div>
        </div>
      </div>
    </>
  );
};

export default ReceiptExpense;
